import React from 'react';
import { ModalsKeys } from 'global';
import { useSelector } from 'react-redux';
import { useModal } from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import { Cancel, Save } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { Box, Button, Stack, Typography, Divider } from '@mui/material';
import { UserProfile, DealActivityEventType, DealActivityEventPriority } from '@trnsact/trnsact-shared-types';
import {
  DateField,
  InputField,
  SelectField,
  DateTimeField,
  CheckboxField,
  AutocompleteValueField,
} from 'components/form';

import { useDealActivityMutations } from '../../../api';
import { CalendarFormMapper } from '../../mapper';
import { calendarApi, useCalendarFormOptions } from '../../api';
import { createEventFormSchema, CreateEventFormValues, createEventFormDefaultValues } from './createEventFormSchema';

export const CreateEventDialog = () => {
  const { isOpen, handleClose } = useModal(ModalsKeys.CreateEventDialog);
  const { handleCreateDealActivity, isDealActivityCreating } = useDealActivityMutations();
  const { accountOptions, contactOptions, accountOptionsLoading, contactOptionsLoading } = useCalendarFormOptions();

  const userProfile = useSelector<any, UserProfile>(state => state.userProfile);

  const { control, watch, setValue, handleSubmit } = useForm<CreateEventFormValues>({
    resolver: zodResolver(createEventFormSchema),
    defaultValues: { ...createEventFormDefaultValues, assignedTo: userProfile.fullName ?? '' },
  });

  const allDay = watch('allDay');

  const onSubmit = async (data: CreateEventFormValues) => {
    await handleCreateDealActivity(CalendarFormMapper.mapFormDtoToCreate(data), () => ({
      awaitRefetchQueries: true,
      refetchQueries: [calendarApi.calendarEventsListing, calendarApi.listOfCalendarEvents],
    }));

    handleClose();
  };

  return (
    <CustomDialog
      maxWidth="md"
      label="New Event"
      isOpen={isOpen}
      onClose={handleClose}
      actions={
        <Stack direction="row" gap="1rem" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit(onSubmit)}
            loading={isDealActivityCreating}
          >
            Create
          </Button>

          <Button onClick={handleClose} startIcon={<Cancel />} disabled={isDealActivityCreating}>
            Cancel
          </Button>
        </Stack>
      }
    >
      <Stack gap="1rem">
        <Stack gap="0.5rem">
          <Typography>Calendar Details</Typography>

          <Stack height="100%" direction="column" gap="1rem">
            <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
              <InputField
                label="Subject"
                name="subject"
                control={control}
                inputProps={{
                  fullWidth: true,
                  variant: 'outlined',
                  required: true,
                }}
              />

              <InputField
                name="assignedTo"
                control={control}
                label="Assigned To"
                inputProps={{
                  disabled: true,
                  fullWidth: true,
                  variant: 'outlined',
                  required: true,
                }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
              <SelectField
                required
                label="Type"
                name="eventType"
                control={control}
                variant="outlined"
                options={convertEnumToSelectOptions(DealActivityEventType)}
              />

              <SelectField
                required
                name="priority"
                label="Priority"
                control={control}
                variant="outlined"
                options={convertEnumToSelectOptions(DealActivityEventPriority)}
              />
            </Box>

            <CheckboxField
              label="All day"
              name="allDay"
              control={control}
              extraAction={checked => {
                if (checked) {
                  setValue('startAt', null);
                  setValue('endAt', null);
                } else {
                  setValue('startDate', null);
                  setValue('endDate', null);
                }
              }}
            />

            <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
              {allDay ? (
                <DateField
                  label="Start date"
                  name="startDate"
                  control={control}
                  slotProps={{
                    textField: { variant: 'outlined', size: 'small' },
                  }}
                />
              ) : (
                <DateTimeField
                  label="Start"
                  name="startAt"
                  control={control}
                  slotProps={{
                    textField: { variant: 'outlined', size: 'small' },
                  }}
                />
              )}

              {allDay ? (
                <DateField
                  label="End date"
                  name="endDate"
                  control={control}
                  slotProps={{
                    textField: { variant: 'outlined', size: 'small' },
                  }}
                />
              ) : (
                <DateTimeField
                  label="End"
                  name="endAt"
                  control={control}
                  slotProps={{
                    textField: { variant: 'outlined', size: 'small' },
                  }}
                />
              )}
            </Box>

            <InputField
              label="Description"
              name="description"
              control={control}
              inputProps={{
                minRows: 3,
                multiline: true,
                fullWidth: true,
                variant: 'outlined',
                placeholder: 'Add description...',
              }}
            />
          </Stack>
        </Stack>

        <Divider />

        <Stack gap="0.5rem">
          <Typography>Related To (Contact)</Typography>

          <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
            <AutocompleteValueField
              name="accountId"
              control={control}
              options={accountOptions}
              loading={accountOptionsLoading}
              textFieldProps={{
                label: 'Account',
                variant: 'outlined',
                size: 'small',
                required: true,
              }}
            />

            <AutocompleteValueField
              name="contactId"
              control={control}
              options={contactOptions}
              loading={contactOptionsLoading}
              textFieldProps={{
                label: 'Contact',
                variant: 'outlined',
                size: 'small',
              }}
            />
          </Box>
        </Stack>
      </Stack>
    </CustomDialog>
  );
};
